'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Grip, ChevronDown, Globe, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/components/LanguageProvider';

export default function CompanyHeader() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { t } = useLanguage();
  const tc = t.companyHeader;

  const GAMES = [
    {
      id: 'survival',
      title: tc.games.survivalTitle,
      desc: tc.games.survivalDesc,
      icon: '🌍',
      url: '/',
      color: 'hover:bg-green-500/10 hover:border-green-500/50',
      textColor: 'group-hover:text-green-400'
    },
    {
      id: 'creative',
      title: tc.games.creativeTitle,
      desc: tc.games.creativeDesc,
      icon: '🎨',
      url: '/creative',
      color: 'hover:bg-purple-500/10 hover:border-purple-500/50',
      textColor: 'group-hover:text-purple-400'
    },
    {
      id: 'RoyoKartRacing',
      title: tc.games.kartTitle,
      desc: tc.games.kartDesc,
      icon: '🏎️',
      url: '#',
      color: 'hover:bg-red-500/10 hover:border-red-500/50',
      textColor: 'group-hover:text-red-400',
      disabled: true
    },
    {
      id: 'rpg',
      title: tc.games.rpgTitle,
      desc: tc.games.rpgDesc,
      icon: '⚔️',
      url: '#',
      color: 'hover:bg-blue-500/10 hover:border-blue-500/50',
      textColor: 'group-hover:text-blue-400',
      disabled: true
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleMenuToggle = (e: Event) => {
      setIsMobileMenuOpen((e as CustomEvent).detail);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('mobile-menu-toggle', handleMenuToggle);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('mobile-menu-toggle', handleMenuToggle);
    };
  }, []);

return (
    <div 
      ref={containerRef}
      className={`absolute top-0 left-0 right-0 ${
        isMobileMenuOpen ? 'z-0 lg:z-[170]' : 'z-[170]'
      } flex flex-col font-inter`}
    >
      <style dangerouslySetInnerHTML={{__html: `
        .dawn-banner__bee {
          position: absolute;
          top: 6px;
          width: 36px;
          height: 36px;
          background-image: url('/dawn-bee.png');
          background-size: 1600% 100%;
          image-rendering: pixelated;
          opacity: 0.5;
          pointer-events: none;
          animation: beeFly 15s linear infinite, beeFrame 1s steps(16) infinite, beeBob 2.5s ease-in-out infinite;
        }
        .dawn-banner__bee--1 { animation-delay: 0s, 0s, 0s; left: -50px; }
        .dawn-banner__bee--2 { animation-delay: -7.5s, 0s, -1.25s; left: -50px; }

        @keyframes beeFly {
          0% { left: -50px; transform: scaleX(1); }
          49.99% { left: 100%; transform: scaleX(1); }
          50% { left: 100%; transform: scaleX(-1); }
          99.99% { left: -50px; transform: scaleX(-1); }
          100% { left: -50px; transform: scaleX(1); }
        }
        @keyframes beeFrame { to { background-position: -1600% 0; } }
        @keyframes beeBob {
          0%, 100% { margin-top: 0; }
          50% { margin-top: -6px; }
        }

        .dawn-banner__cta {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 5px 14px;
          background: #61b946;
          border: 3px solid;
          border-color: #95e465 #286430 #286430 #95e465;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: #fff;
          text-shadow: 0 1px 0 rgba(0, 0, 0, .25);
          white-space: nowrap;
          transition: filter .15s ease;
          flex-shrink: 0;
        }
        .dawn-banner__cta:hover {
          filter: brightness(1.1);
        }
      `}} />

      <div className="relative z-20 h-10 w-full bg-[#111111] border-b border-white/5">
        <div className="h-full w-full max-w-[120rem] mx-auto px-4 md:px-14 xl:px-[11.25rem] flex items-center justify-between">
          <div className="flex items-center h-full">
            <button 
              onClick={() => setIsOpen(!isOpen)}
              className={`
                cursor-pointer flex items-center gap-3 h-full px-3 -ml-3 mr-4
                text-[11px] font-bold tracking-widest uppercase text-white/70 
                hover:bg-white/5 hover:text-white transition-all outline-none
                ${isOpen ? 'bg-white/10 text-white' : ''}
              `}
            >
              <Grip size={16} className={isOpen ? 'text-[#667fff]' : ''} />
              <span className="pt-0.5">Royolands</span>
              <ChevronDown 
                size={12} 
                className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
              />
            </button>

            <div className="w-[1px] h-4 bg-white/10 mr-4 hidden sm:block" />

            <Link href="/" className="hidden sm:flex items-center gap-2 text-[11px] font-semibold text-white/50 hover:text-white transition-colors cursor-pointer">
              <Globe size={12} />
              RPLANDS
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <a 
              href="https://dsc.gg/rplands" 
              target="_blank" 
              className="text-[10px] font-bold text-white/40 hover:text-white transition-colors uppercase tracking-wide flex items-center gap-1 cursor-pointer outline-none"
            >
              {tc.nav.support}
              <ExternalLink size={10} />
            </a>
            <div className="w-[1px] h-3 bg-white/10" />
            <div className="flex items-center gap-2">
               <span className="w-2 h-2 rounded-full bg-[#4cc91f] animate-pulse shadow-[0_0_8px_rgba(76,201,31,0.6)]"></span>
               <span className="text-[10px] font-bold text-white/60">{tc.nav.systemsNormal}</span>
            </div>
          </div>
        </div>
      </div>

      <a 
        href={tc.banner.link} 
        {...(tc.banner.link.startsWith('http') ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className="relative z-10 block w-full bg-[#18181b] hover:bg-[#1f1f23] transition-colors border-b border-white/5 overflow-hidden group outline-none"
      >
        <div className="dawn-banner__bee dawn-banner__bee--1" aria-hidden="true"></div>
        <div className="dawn-banner__bee dawn-banner__bee--2" aria-hidden="true"></div>
        
        <div className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 py-2 sm:py-2.5 px-4 mx-auto max-w-[120rem]">
          <span className="text-white/80 text-[11px] sm:text-xs font-medium text-center">
            {tc.banner.text1}
            <strong className="text-white">{tc.banner.highlight1}</strong>
            {tc.banner.text2}
            <strong className="text-white">{tc.banner.highlight2}</strong>
            {tc.banner.text3}
          </span>
          <span className="dawn-banner__cta">
            {tc.banner.button}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7"></path><path d="M7 7h10v10"></path></svg>
          </span>
        </div>
      </a>

      {/* Выпадающее меню проектов */}
      <div className={`
        absolute top-full left-0 w-full bg-[#111111]/95 backdrop-blur-xl border-b border-white/10
        overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.19,1,0.22,1)]
        ${isOpen ? 'max-h-[400px] opacity-100 shadow-2xl' : 'max-h-0 opacity-0'}
      `}>
        <div className="max-w-[120rem] mx-auto px-4 md:px-14 xl:px-[11.25rem] py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {GAMES.map((game) => (
              <Link 
                key={game.id}
                href={game.url}
                onClick={() => !game.disabled && setIsOpen(false)}
                className={`
                  group relative p-4 rounded-xl border border-white/5 bg-white/[0.02]
                  transition-all duration-300 outline-none
                  ${game.disabled ? 'opacity-50 cursor-not-allowed' : `cursor-pointer ${game.color} hover:-translate-y-1`}
                `}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-2xl filter drop-shadow-md group-hover:scale-110 transition-transform duration-300">
                    {game.icon}
                  </span>
                  {game.disabled && (
                    <span className="text-[9px] font-bold bg-white/10 px-1.5 py-0.5 rounded text-white/40">{tc.games.devTag}</span>
                  )}
                </div>
                <h3 className={`text-sm font-bold text-white mb-1 transition-colors ${!game.disabled ? game.textColor : ''}`}>
                  {game.title}
                </h3>
                <p className="text-xs text-white/40 group-hover:text-white/60 transition-colors">
                  {game.desc}
                </p>
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl pointer-events-none" />
              </Link>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-white/5 flex justify-center">
             <Link 
                href="https://royolands.com/projects" 
                onClick={() => setIsOpen(false)}
                className="text-xs font-bold text-white/30 hover:text-white transition-colors tracking-widest uppercase flex items-center gap-2 cursor-pointer outline-none"
             >
                {tc.nav.allProjects} <span className="text-lg leading-none">›</span>
             </Link>
          </div>
        </div>
      </div>
    </div>
  );
}